import React, { useState } from 'react';
import styled from 'styled-components';
import { Container, Heading, Text } from '../styles/components';
import Tag from './Tag';

const Section = styled.section`
  padding: ${props => props.theme.spacing.xxl} 0;
  background-color: ${props => props.theme.colors.backgroundAlt};
  overflow: hidden;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  overflow-x: auto;
  padding-bottom: ${props => props.theme.spacing.sm};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
  }
`;

const TabButton = styled.button`
  background: ${props => props.$active ? props.theme.colors.brand : props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  border: 2px solid ${props => props.$active ? props.theme.colors.brand : props.theme.colors.border};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.radii.round};
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.body};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.default};
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.theme.colors.brand};
    border-color: ${props => props.theme.colors.brand};
  }
`;

const IntroLine = styled(Text)`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-size: ${props => props.theme.typography.sizes.h4};
  color: ${props => props.theme.colors.secondary};
  margin-bottom: ${props => props.theme.spacing.xl};
  opacity: 0;
  transform: translateY(20px);
  animation: fadeIn 0.5s ease forwards;
  
  @keyframes fadeIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ContentBox = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.radii.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.md};
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    position: relative;
    padding-left: ${props => props.theme.spacing.xl};
    margin-bottom: ${props => props.theme.spacing.md};
    
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: ${props => props.$positive ? props.theme.colors.brand : props.theme.colors.secondary};
    }
  }
`;

const audienceData = {
  solopreneur: {
    icon: '🎯',
    title: 'Solopreneurs & Specialists',
    intro: "You're amazing at what you do. But your genius is trapped in your head.",
    dailyGrind: [
      'Creating content that drifts off-message because you\'re rushed',
      'Copy-pasting the same info into prompt templates and chat context - and still spending hours prompt engineering it',
      'Hunting through files for info to brief contractors',
      'Losing great ideas in notebooks and random documents',
      'Getting stuck in execution instead of growing strategically',
      'Strategy drift - where things change unintentionally and you lose track of how everything hangs together'
    ],
    solutions: [
      'Turn your expertise into consistent, strategic action',
      'Create content faster without losing your voice',
      'No more copy-pasting or prompt wrangling',
      'Keep everything aligned and updated as you grow',
      'Brief contractors in minutes, not hours - or give them access and let them discover your strategy themselves!',
      'Stay executing on what works while rapidly exploring new opportunities'
    ]
  },
  smallTeam: {
    icon: '👥',
    title: 'Small teams',
    intro: 'Your team is great. But you know your brand could be more consistent and things could run smoother',
    dailyGrind: [
      'New team members take months to truly "get it"',
      'Content and messaging drifts between team members',
      'Hours lost in meetings explaining the same things',
      'Strategy updates don\'t reach everyone effectively',
      'It takes too long to \'turn the ship around\' when things change'
    ],
    solutions: [
      'One source of truth for your whole team',
      'Onboard new team members in days, not months',
      'Get the holy grail of consistent messaging - in just a few clicks',
      'Speed up drafting to approval - with fewer edits and less explaining',
      'Scale operations without losing control',
      'Bring in amazing marketers/contractors even if they don\'t have domain knowledge'
    ]
  },
  microagency: {
    icon: '🚀',
    title: 'Microagencies',
    intro: 'Your clients love you! But the mental overhead of managing multiple strategies is taking its toll on your growth',
    dailyGrind: [
      'Recreating the same strategic work - in static, hard to update files - for every client',
      'Difficulty handing off strategy for client execution',
      'Trying to manage a dozen \'projects\' in AI accounts that quickly get out of date',
      'Hours lost in repetitive client education',
      'Struggling to maintain quality as you scale',
      'Constant context-switching between clients'
    ],
    solutions: [
      'Manage multiple client strategies effortlessly',
      'Create reusable prompt or output templates - and just plug and play different client strategy',
      'Scale your delivery without growing headcount',
      'Add instant value clients can see',
      'Resell Moogul at a profit - while still giving your clients a great deal'
    ]
  }
};

const AudienceTabsSection = () => {
  const [activeTab, setActiveTab] = useState('solopreneur');
  const currentData = audienceData[activeTab];

  return (
    <Section>
      <Container>
        <Tag>WHO IS MOOGUL FOR?</Tag>
        <Heading as="h2" $mb={props => props.theme.spacing.xl}>
          Find your perfect fit
        </Heading>
        
        <TabsContainer>
          {Object.entries(audienceData).map(([key, data]) => (
            <TabButton
              key={key}
              $active={activeTab === key}
              onClick={() => setActiveTab(key)}
            >
              {data.icon} {data.title}
            </TabButton>
          ))}
        </TabsContainer>

        <IntroLine key={activeTab}>
          {currentData.intro}
        </IntroLine>

        <ContentGrid>
          <ContentBox>
            <Heading as="h3" $mb={props => props.theme.spacing.lg}>
              The daily grind
            </Heading>
            <List>
              {currentData.dailyGrind.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </List>
          </ContentBox>

          <ContentBox>
            <Heading as="h3" $mb={props => props.theme.spacing.lg}>
              How Moogul sets you free
            </Heading>
            <List $positive>
              {currentData.solutions.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </List>
          </ContentBox>
        </ContentGrid>
      </Container>
    </Section>
  );
};

export default AudienceTabsSection; 